/**
 * Currency / number / date formatters.
 *
 * Money uses the Mercury-style "superscript cents" pattern — see Money.tsx.
 * These helpers return the integer and decimal parts separately so the
 * component can render them at different sizes.
 */

const SYMBOLS: Record<string, string> = {
  BDT: '৳',
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
};

export function currencySymbol(code = 'BDT'): string {
  return SYMBOLS[code] ?? code + ' ';
}

/** Splits an amount into "integer part with thousand separators" + "two-digit cents". */
export function splitAmount(amount: number): { sign: string; integer: string; cents: string } {
  // Defensive: real API data can be null/undefined/NaN where a mock never was —
  // treat it as 0 rather than rendering "$NaN".
  const safe = Number.isFinite(amount) ? amount : 0;
  const sign = safe < 0 ? '−' : '';
  const abs = Math.abs(safe);
  const integer = Math.trunc(abs).toLocaleString('en-US');
  const cents = Math.round((abs - Math.trunc(abs)) * 100)
    .toString()
    .padStart(2, '0');
  return { sign, integer, cents };
}

/** Compact human numbers for KPI tiles: 1234 → "1,234"; 28500 → "28,500" */
export function formatInt(n: number): string {
  // Defensive: a missing/loading KPI is 0, not a thrown "cannot read toLocaleString".
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-US');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "03 May 2026" */
export function formatDate(d: Date | string, opts?: { compact?: boolean }): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—'; // null/invalid → dash, not "Invalid Date"
  const day = date.getDate().toString().padStart(2, '0');
  const month = MONTHS[date.getMonth()] ?? '';
  const year = date.getFullYear();
  if (opts?.compact && date.getFullYear() === new Date().getFullYear()) {
    return `${day} ${month}`;
  }
  return `${day} ${month} ${year}`;
}

/** "14:32" — UTC time-of-day from an ISO timestamp (for 24h logs). */
export function formatTime(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(11, 16);
}

/** "5 minutes ago", "2 days ago", "3 May" */
export function timeAgo(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86_400 * 7) return `${Math.floor(sec / 86_400)}d ago`;
  return formatDate(date, { compact: true });
}

/** "IK" from "Imran Khan", "T" from "Trader" */
export function initials(name: string, max = 2): string {
  return (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
